/**
 * Production adapter for `move_structure`: the SAME steps the Transform
 * tool's move performs on mouse release (`StructureTranslateAction`), using
 * its public helpers — but on a COPY of the structure, so the card can
 * preview the result before anything changes:
 *   1. snapshot the texture grid and pixels (`flattenSlices`);
 *   2. shift the grid by the translation (`Grid.transform`);
 *   3. snap it to the image voxel lattice (`buildSnappedGrid`);
 *   4. resample the pixels onto it (`resampleUint8Grid`, background 0)
 *      into a copy (`ContourTexture.cloneWithNewSdf`) — the structure itself
 *      is not touched.
 * Hover shows the copy with the existing preview overlay
 * (`createPreviewOverlayPort`). Apply adopts it with
 * `roi.replaceTexture(copy, 'translate')` — one undo step with the same
 * label as the mouse move — then refreshes the contour layer; like the
 * mouse move, no message pops up. Reject frees the copy.
 *
 * The fit check uses the action's own `clampTranslationToImageBounds`, but
 * only to MEASURE the room left (a very long move clamped = the room); the
 * planner refuses rather than clamps. Browser glue covered by the manual
 * demo; the logic is unit-tested through fakes.
 */
import { mat4, vec3 } from 'gl-matrix';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { resampleUint8Grid } from '~/domain/services/resampleGrid';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    buildSnappedGrid,
    clampTranslationToImageBounds,
    flattenSlices,
    unflattenToSlices,
} from '~/engine/tool/actions/transform-actions/transformUtils';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import { createPreviewOverlayPort } from '../core/previewOverlay';
import { DIRECTION_VECTORS, type MoveDeps } from './toolRunner';

/** A move far longer than any scan, used to measure the room left. */
const PROBE_DISTANCE_MM = 1e5;
/** The mouse move's undo label (`roi.startModify('translate')`). */
const UNDO_LABEL = 'translate';

export function useMoveStructureDeps(): () => Omit<
    MoveDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);

    return useCallback(() => {
        const overlay = createPreviewOverlayPort(env);
        const requireTexture = (roiNumber: number) => {
            const roi = env?.getRoi(roiNumber);
            const texture = roi?.texture;
            if (!roi || !texture || texture.isEmpty) {
                throw new Error('the structure has no contours to move');
            }
            return { roi, texture };
        };
        return {
            structures: projectStructureFacts(structureSet, env),
            canModify: (roiNumber: number) =>
                env?.getRoi(roiNumber)?.canModify ?? false,
            // Read live: `env` is the mutable singleton.
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            editVersion: (roiNumber: number) =>
                env?.getRoi(roiNumber)?.sdfVersion,
            maxDistanceMm: (roiNumber, direction) => {
                const { texture } = requireTexture(roiNumber);
                const unit = DIRECTION_VECTORS[direction];
                const probe = vec3.fromValues(
                    unit[0] * PROBE_DISTANCE_MM,
                    unit[1] * PROBE_DISTANCE_MM,
                    unit[2] * PROBE_DISTANCE_MM
                );
                const room = clampTranslationToImageBounds(
                    probe,
                    texture.textureGrid,
                    texture.grid
                );
                const along = vec3.dot(room, vec3.fromValues(...unit));
                // Not clamped: the structure already exceeds the image on
                // this axis, and the engine allows free movement.
                return along >= PROBE_DISTANCE_MM - 1 ? Infinity : along;
            },
            centreMm: (roiNumber) => {
                const c = env?.getRoiStatistics(roiNumber)?.centerOfMassMm;
                return c ? ([c[0], c[1], c[2]] as const) : undefined;
            },
            geometry: {
                computeMove: async (roiNumber, deltaMm) => {
                    const { texture } = requireTexture(roiNumber);
                    const originalGrid = texture.textureGrid.clone();
                    const originalFlatPixels = flattenSlices(
                        texture.pixels,
                        originalGrid
                    );
                    const translatedGrid = originalGrid.transform(
                        mat4.fromTranslation(
                            mat4.create(),
                            vec3.fromValues(...deltaMm)
                        )
                    );
                    const snappedGrid = buildSnappedGrid(
                        translatedGrid,
                        texture.grid
                    );
                    const result = resampleUint8Grid(
                        originalFlatPixels,
                        translatedGrid,
                        snappedGrid,
                        { backgroundValue: 0 }
                    );
                    // A copy: the structure's own texture is untouched.
                    const moved = texture.cloneWithNewSdf(
                        unflattenToSlices(
                            result.pixels,
                            snappedGrid.sizeX,
                            snappedGrid.sizeY,
                            snappedGrid.sizeZ
                        ),
                        snappedGrid
                    );
                    const c = moved.statistics.centerOfMassMm;
                    return {
                        handle: moved,
                        // Lazy, like toComputedGeometry.
                        get resultVolumeCm3() {
                            return moved.volumeCm3;
                        },
                        centreMm: c ? ([c[0], c[1], c[2]] as const) : undefined,
                    };
                },
                releaseComputed: (handle) => {
                    (handle as ContourTexture | undefined)?.delete();
                },
            },
            commit: {
                replaceInPlace: (roiNumber, computed) => {
                    const roi = env?.getRoi(roiNumber);
                    const set = env?.structureSet;
                    if (!roi || !set) {
                        throw new Error('the structure is no longer loaded');
                    }
                    roi.replaceTexture(
                        computed.handle as ContourTexture,
                        UNDO_LABEL
                    );
                    ViewManager.viewUpdateEvent(
                        new VolumeViewUpdateEvent(
                            set,
                            LayerBit.CONTOUR_LAYER_BIT,
                            true
                        )
                    );
                    return roi.volumeCm3;
                },
            },
            preview: {
                show: (computed) =>
                    overlay.show(computed.handle as ContourTexture),
                hide: () => overlay.hide(),
            },
        };
    }, [env, structureSet]);
}
